import { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import { Button } from 'reactstrap';
import { ReactComponent as MSLogo } from 'assets/icons/ms-logo.svg';
import { CompanyDto } from 'utils/wemble-api.generated';
import axios from 'axios';
import { useHistory, useLocation } from 'react-router-dom';
import organization from 'utils/organization';

const useStyles = makeStyles(() => ({
  container: {
    marginTop: 40,
    marginBottom: 40,
  },
  section: {
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    padding: 24,
    marginBottom: 16,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 20,
    '& svg': {
      marginRight: 12,
      width: 24,
      height: 24,
    },
  },
  optionsContainer: {
    display: 'flex',
    gap: 24,
    marginTop: 20,
  },
  optionCard: {
    flex: 1,
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    padding: 20,
    cursor: 'pointer',
    transition: 'all 0.2s',
    '&:hover': {
      borderColor: '#0078d4',
      boxShadow: '0 2px 8px rgba(0,120,212,0.1)',
    },
  },
  optionTitle: {
    fontSize: 18,
    fontWeight: 600,
    marginBottom: 12,
  },
  optionDescription: {
    fontSize: 14,
    color: '#666',
    marginBottom: 16,
  },
  featureList: {
    listStyle: 'none',
    padding: 0,
    margin: '16px 0',
    '& li': {
      fontSize: 14,
      marginBottom: 8,
      paddingLeft: 20,
      position: 'relative',
      '&:before': {
        content: '"✓"',
        position: 'absolute',
        left: 0,
        color: '#0078d4',
        fontWeight: 'bold',
      },
    },
  },
  contactNote: {
    fontSize: 12,
    color: '#999',
    fontStyle: 'italic',
  },
  tenantInfo: {
    backgroundColor: '#f5f5f5',
    padding: 16,
    borderRadius: 4,
    marginTop: 16,
    '& strong': {
      display: 'block',
      marginBottom: 8,
    },
    '& code': {
      backgroundColor: '#fff',
      padding: '4px 8px',
      borderRadius: 4,
      fontSize: 14,
    },
  },
  buttonContainer: {
    marginTop: 16,
  },
  permissionCard: {
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
    backgroundColor: '#fff',
  },
  permissionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  permissionName: {
    fontFamily: 'monospace',
    fontSize: 14,
    fontWeight: 600,
    color: '#333',
  },
  permissionStatus: {
    fontSize: 14,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  permissionDescription: {
    fontSize: 13,
    color: '#666',
    lineHeight: 1.5,
  },
  permissionType: {
    fontSize: 11,
    color: '#999',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginTop: 4,
  },
}));

// Microsoft sends the admin back to /groups with the `state` it was given. Only the return of a connect
// this tab started (the random state it stored on the click) links a tenant: a link alone, carrying
// someone else's tenant or code, does nothing.
const STATE_KEY = 'entraConnectState';
export const newEntraState = (prefix = '') => {
  const state = prefix + Array.from(window.crypto.getRandomValues(new Uint32Array(4)), (n) => n.toString(36)).join('');
  sessionStorage.setItem(STATE_KEY, state);
  return state;
};
export const isOwnEntraState = (state: string | null | undefined) => {
  try {
    const expected = sessionStorage.getItem(STATE_KEY);
    sessionStorage.removeItem(STATE_KEY);
    return !!state && state === expected;
  } catch {
    return false;
  }
};

interface MicrosoftEntraSetupProps {
  company?: CompanyDto;
  onRefetch: () => void;
  onRemoveTenant?: () => void;
}

const MicrosoftEntraSetup = ({ company, onRefetch, onRemoveTenant }: MicrosoftEntraSetupProps) => {
  const classes = useStyles();
  const history = useHistory();
  const { search, pathname } = useLocation();
  const [selectedOption, setSelectedOption] = useState<'default' | 'custom' | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    const code = searchParams.get('code');
    const state = searchParams.get('state');

    if (code && company?._id) {
      if (!isOwnEntraState(state)) {
        history.replace(pathname);
        return;
      }
      setIsProcessing(true);
      const redirectUri = `${window.location.origin}/groups`;

      axios
        .post('/api/v2/azure-ad/custom-setup', {
          code,
          redirectUri,
        })
        .then(() => {
          onRefetch();
          history.replace(pathname);
        })
        .catch((error) => {
          console.error('Custom setup failed:', error);
          alert('Failed to complete custom setup. Please try again.');
        })
        .finally(() => {
          setIsProcessing(false);
        });
    }
  }, [search, pathname, history, company, onRefetch]);


  const handleDefaultSetup = () => {
    const clientId = process.env.REACT_APP_AAD_APP_CLIENT_ID;
    if (clientId) {
      const redirectUri = `${window.location.origin}${pathname}`;
      window.location.href = `https://login.microsoftonline.com/common/adminconsent?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&state=${newEntraState()}`;
    }
  };

  const handleCustomSetup = () => {
    const clientId = process.env.REACT_APP_AAD_APP_CLIENT_ID;
    if (clientId) {
      // Minimal OAuth flow with only user.read permission
      const redirectUri = `${window.location.origin}/groups`;
      window.location.href = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${clientId}&response_type=code&redirect_uri=${encodeURIComponent(redirectUri)}&response_mode=query&scope=User.Read&state=${newEntraState('custom_setup.')}`;
    }
  };

  if (!process.env.REACT_APP_AAD_APP_CLIENT_ID) {
    return null;
  }

  if (isProcessing) {
    return (
      <div className={classes.container}>
        <div className={classes.section}>
          <div className={classes.header}>
            <MSLogo />
            <h3 style={{ margin: 0 }}>Microsoft Entra ID Setup</h3>
          </div>
          <p>Processing custom setup...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.section}>
        <div className={classes.header}>
          <MSLogo />
          <h3 style={{ margin: 0 }}>Microsoft Entra ID Setup</h3>
        </div>

        {company?.azureADTenantId ? (
          <>
            <div className={classes.tenantInfo}>
              <strong>Connected Tenant</strong>
              <code>{company.azureADTenantId}</code>
            </div>

            {/* Required Permissions */}
            <div style={{ marginTop: 24 }}>
              <h4 style={{ margin: '0 0 16px 0' }}>Permissions</h4>
              <p style={{ fontSize: 14, color: '#666', marginBottom: 16 }}>
                The following permissions can be used by Wemble:
              </p>

              {/* User.Read - Delegated */}
              <div className={classes.permissionCard}>
                <div className={classes.permissionHeader}>
                  <span className={classes.permissionName}>User.Read</span>
                </div>
                <div className={classes.permissionDescription}>
                  Needed for users to log in to Wemble through SSO.
                </div>
                <div className={classes.permissionType}>Delegated Permission (Admin Consent Required)</div>
              </div>

              {/* User.Read.All - Application */}
              {organization(company?._id).name !== "Mannheimer Swartling" && (
                <div className={classes.permissionCard}>
                  <div className={classes.permissionHeader}>
                    <span className={classes.permissionName}>User.Read.All</span>
                  </div>
                  <div className={classes.permissionDescription}>
                    Needed for automatic bulk sync of all users' profile pictures and group memberships.
                  </div>
                  <div className={classes.permissionType}>Application Permission</div>
                </div>)}

            </div>

            {onRemoveTenant && (
              <div className={classes.buttonContainer}>
                <Button color="danger" onClick={onRemoveTenant}>
                  Remove Tenant ID from Wemble
                </Button>
                <p style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
                  Note: This only removes the connection in Wemble. To fully disconnect, also remove Wemble from your Entra ID admin center.
                </p>
              </div>
            )}
          </>
        ) : (
          <>
            <p style={{ marginBottom: 24, color: '#666' }}>
              Choose how you want to configure Microsoft Entra ID integration with Wemble:
            </p>

            <div className={classes.optionsContainer}>
              {/* Default Configuration */}
              <div
                className={classes.optionCard}
                onMouseEnter={() => setSelectedOption('default')}
                style={{
                  borderColor: selectedOption === 'default' ? '#0078d4' : undefined,
                }}
              >
                <div className={classes.optionTitle}>Default Configuration</div>
                <div className={classes.optionDescription}>
                  One-click setup with full Entra functionality
                </div>

                <ul className={classes.featureList}>
                  <li>Login through SSO automatically triggered in Teams and Outlook apps</li>
                  <li>Automatic sync of profile pictures and groups for all users <span className={classes.contactNote}>(contact Wemble support to enable)</span></li>
                  <li>
                    Automatic just-in-time user provisioning <span className={classes.contactNote}>(contact Wemble support to enable)</span>
                  </li>
                </ul>

                <div className={classes.buttonContainer}>
                  <Button
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDefaultSetup();
                    }}
                  >
                    Connect with Full Permissions
                  </Button>
                </div>
              </div>

              {/* Minimal Setup */}
              <div
                className={classes.optionCard}
                onMouseEnter={() => setSelectedOption('custom')}
                style={{
                  borderColor: selectedOption === 'custom' ? '#0078d4' : undefined,
                }}
              >
                <div className={classes.optionTitle}>Minimal Setup</div>
                <div className={classes.optionDescription}>
                  Basic SSO login only - does not allow automatic sync of profile pictures or groups
                </div>

                <ul className={classes.featureList}>
                  <li>Login through SSO automatically triggered in Teams and Outlook apps</li>
                  <li>Adds Wemble as an Enterprise Application in your Entra ID</li>
                </ul>

                <div style={{
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: 4,
                  padding: 12,
                  marginTop: 12,
                  fontSize: 13
                }}>
                  <strong>⚠️ Important:</strong> You must be a <strong>Global Administrator</strong> to complete this setup.
                  <div style={{ marginTop: 6 }}>
                    Make sure to check <strong>"Consent on behalf of your organization"</strong> when prompted.
                  </div>
                  <div style={{ marginTop: 4, fontSize: 12, color: '#856404' }}>
                    Access can be limited in Entra by requiring user assignment.
                  </div>
                </div>

                <div className={classes.buttonContainer}>
                  <Button
                    color="secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCustomSetup();
                    }}
                  >
                    Add to Entra ID
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MicrosoftEntraSetup;
